import React, { useState, useEffect } from 'react';
import { Button, Form, Row, Col, Modal, Navbar, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashRestore, faTrash } from '@fortawesome/free-solid-svg-icons';
import Fuse from 'fuse.js';
import {
    BulkDeleteArbCase, BulkUpsertArbCase, GenerateArbCases, GetAllArbCases, GetArbCase,
    GetArbitrationPlayerPresenters, GetArbitrationPlayerRepresentatives, GetArbitrationTeamRepresentatives, GetArbitrators,
    GetDataEntryReaders, GetDataEntryWriters, GetPlayers, GetStatFormInputs, UpsertArbCase, AddNewPerson
} from '../../Services/ApiSvc';
import AutoComplete from '../Common/AutoComplete';
import { customDateString, customTimeString, getEasternTimeFormatter } from '../Common/CommonFunctions';
let curSeason = (new Date()).getMonth() >= 2 ? (new Date()).getFullYear() + 1 : (new Date()).getFullYear();

function ArbDataEntry() {
    const [allArbCases, setAllArbCases] = useState(null);
    const [caseSelected, setCaseSelected] = useState({ season: null, playerId: null });
    const [data, setData] = useState({
        arbCase: null,
        exceptionMsg: null,
        loading: false,
    });
    const [lookups, setLookups] = useState(null);
    const [newPerson, setNewPerson] = useState(null);
    const [saveResponse, setSaveResponse] = useState(null);
    const [tabName, setTabName] = useState('individual');
    const [updatedCase, setUpdatedCase] = useState(null);
    const [userPermissions, setUserPermissions] = useState(null);

    useEffect(() => {
        (async function Initialize() {
            let dataReaders = (await GetDataEntryReaders()).map(username => username.toLowerCase());
            let dataWriters = (await GetDataEntryWriters()).map(username => username.toLowerCase());
            let username = JSON.parse(localStorage.getItem('user')).username.toLowerCase();
            let allArbCases = await GetAllArbCases();
            let players = await GetPlayers();
            let arbitrators = (await GetArbitrators())
                .map(person => ({ key: person.id, title: person.name }));
            let playerReps = (await GetArbitrationPlayerRepresentatives())
                .map(person => ({ key: person.id, title: person.name }));
            let playerPresenters = (await GetArbitrationPlayerPresenters())
                .map(person => ({ key: person.id, title: person.name }));
            let teamPresenters = (await GetArbitrationTeamRepresentatives())
                .map(person => ({ key: person.id, title: person.name }));
            let teams = (await GetStatFormInputs())
                .find(stat => stat.statId === 3)?.selectOptions
                .map(selectOption => ({ key: Number(selectOption.value), title: selectOption.label }));
            let bonusTypes = (await GetStatFormInputs())
                .find(stat => stat.statId === 787)?.selectOptions
                .map(selectOption => ({ key: Number(selectOption.value), title: selectOption.label }));
            let resultTypes = (await GetStatFormInputs())
                .find(stat => stat.statId === 784)?.selectOptions
                .map(selectOption => ({ key: Number(selectOption.value), title: selectOption.label }));
            let voteTypes = [{ key: 1, title: 'Player' }, { key: 2, title: 'Team' }];

            let seasonsArbCases = {};
            seasonsArbCases[curSeason.toString()] = [];
            allArbCases
                .filter(arbCase => arbCase.playerId > 0)
                .forEach(arbCase => {
                    if (!Object.hasOwn(seasonsArbCases, arbCase.season.toString()))
                        seasonsArbCases[arbCase.season.toString()] = [];
                    let curArbCase = Object.assign({ key: arbCase.playerId, title: arbCase.player }, arbCase);
                    curArbCase.arbCaseResult = resultTypes.find(resultType => resultType.title === arbCase.arbCaseResult)?.key


                    seasonsArbCases[arbCase.season.toString()].push(curArbCase);
                })

            let seasonPlayers = {};
            players.forEach(player => {
                // We need to add one to playerSeason because the players from the previous season should be selectable
                // for a arbitration filing season
                Array(player.maxSeason - player.minSeason + 1).fill(player.minSeason).forEach((playerSeason, index) => {
                    if (!Object.hasOwn(seasonPlayers, playerSeason + index + 1))
                        seasonPlayers[playerSeason + index + 1] = [];
                    let curPlayer = { key: player.id, title: player.nameLastFirst };
                    seasonPlayers[playerSeason + index + 1].push(curPlayer);
                })
            });

            setAllArbCases(seasonsArbCases);
            setCaseSelected({ season: Math.max(...Object.keys(seasonsArbCases).map(season => Number(season))), playerId: null });
            setLookups({
                arbitrators: [{ key: 0, title: 'No Arbitrator' }, { key: -1, title: 'New Arbitrator' }].concat(arbitrators),
                bonusTypes: [{ key: 0, title: 'No Bonus' }].concat(bonusTypes),
                playerPresenters: [{ key: 0, title: 'No Presenter' }, { key: -1, title: 'New Presenter' }].concat(playerPresenters),
                playerReps: [{ key: 0, title: 'No Representative' }, { key: -1, title: 'New Representative' }].concat(playerReps),
                players: seasonPlayers,
                resultTypes: [{ key: 0, title: 'No Result' }].concat(resultTypes),
                teamPresenters: [{ key: 0, title: 'No Presenter' }, { key: -1, title: 'New Presenter' }].concat(teamPresenters),
                teams: teams,
                voteTypes: voteTypes,
            });
            setUserPermissions({
                dataReader: dataReaders.includes(username),
                dataWriter: dataWriters.includes(username),
            })
        })()
    }, []);

    useEffect(() => {
        setUpdatedCase(data.arbCase);
    }, [data.arbCase])

    useEffect(() => {
        if (!updatedCase)
            return;
        let caseArbitrators = [];

        if (updatedCase.arbitrators?.length > 0)
            caseArbitrators = updatedCase.arbitrators.map(arbitrator => arbitrator.arbitratorId);
        
        if ([updatedCase.playerRepId, updatedCase.playerPresenterId, updatedCase.teamRepId,
        ...caseArbitrators].includes(-1)) {
            if (updatedCase.playerRepId === -1)
                setNewPerson({ label: 'playerRep', id: -1 })
            else if (updatedCase.playerPresenterId === -1)
                setNewPerson({ label: 'playerPresenter', id: -1 })
            else if (updatedCase.teamRepId === -1)
                setNewPerson({ label: 'teamRep', id: -1 })
            else
                updatedCase.arbitrators.forEach((arbitrator, index) => {
                    if (arbitrator.arbitratorId === -1)
                        setNewPerson({ label: 'arbitrator-' + index, id: -1 })
                })
        }
    }, [updatedCase])

    function calculateMidpoint() {
        return (
            (
                Number(updatedCase.arbTeamSubmission.toString().replaceAll(',', ''))
                + Number(updatedCase.arbPlayerSubmission.toString().replaceAll(',', ''))
            )
            / 2
        )
            .toFixed(0)
            .replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function clearData() {
        setUpdatedCase({
            id: updatedCase.id,
            season: updatedCase.season,
            playerId: updatedCase.playerId,
            player: updatedCase.player,
            playerRepId: null,
            playerRep: '',
            playerPresenterId: null,
            playerPresenter: '',
            arbPlayerSubmission: 0,
            teamId: null,
            team: '',
            teamRepId: null,
            teamRep: '',
            arbTeamSubmission: 0,
            margin: null,
            arbHearingDate: null,
            arbCaseResult: 0,
            clubOptions: null,
            arbFinalAmount: 0,
            arbCaseBonus: 0,
            arbitrators: [],
        })
    }

    async function getData(season, playerId) {
        setData({
            arbCase: null,
            exceptionMsg: null,
            loading: true,
        });
        
        let response = {};
        try {
            response = await GetArbCase(season, playerId);
            let arbData = response[0];
            arbData.arbPlayerSubmission = arbData.arbPlayerSubmission.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            arbData.arbTeamSubmission = arbData.arbTeamSubmission.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            arbData.arbFinalAmount = arbData.arbFinalAmount.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            arbData.player = allArbCases[caseSelected.season.toString()].find(player => player.key === arbData.playerId)?.title;
            arbData.playerRep = arbData.playerRepId > 0
                ? lookups.playerReps.find(playerRep => playerRep.key === arbData.playerRepId)?.title
                : '';
            arbData.playerPresenter = arbData.playerPresenterId > 0
                ? lookups.playerPresenters.find(playerPresenter => playerPresenter.key === arbData.playerPresenterId)?.title
                : '';
            arbData.teamRep = arbData.teamRepId > 0
                ? lookups.teamPresenters.find(teamRep => teamRep.key === arbData.teamRepId)?.title
                : '';
            arbData.team = arbData.teamId > 0
                ? lookups.teams.find(team => team.key === arbData.teamId)?.title
                : '';
            arbData.arbCaseResult = lookups.resultTypes.find(resultType => resultType.title === arbData.arbCaseResult)?.key
            arbData.arbCaseBonus = lookups.bonusTypes.find(bonusType => bonusType.title === arbData.arbCaseBonus)?.key
            if (arbData.arbitrators) {
                arbData.arbitrators = arbData.arbitrators.map(arbitrator => {
                    return {
                        arbitrator: lookups.arbitrators.find(person => person.key === arbitrator.arbitratorId)?.title,
                        arbitratorId: arbitrator.arbitratorId,
                        caseVote: lookups.voteTypes.find(voteType => voteType.title === arbitrator.caseVote)?.key,
                        isChair: arbitrator.isChair,
                    }
                })
            }
            const etTimeFormatter = getEasternTimeFormatter();
            if (arbData.arbHearingDate != null) {
                arbData.arbHearingDate = etTimeFormatter.format(new Date(arbData.arbHearingDate + 'Z'));
            }

            setData({
                arbCase: arbData,
                loading: false,
            })
        } catch (ex) {
            setData({
                exceptionMsg: 'There was a problem with the query.',
                loading: false,
            })
        }
    }

    async function submitArbCase() {
        setSaveResponse(null);
        let arbCaseData = [];

        if (updatedCase.arbitrators) {
            updatedCase.arbitrators.forEach(arbitrator => {
                arbCaseData.push({
                    id: updatedCase.id,
                    season: updatedCase.season,
                    playerId: updatedCase.playerId,
                    playerRepId: updatedCase.playerRepId,
                    playerPresenterId: updatedCase.playerPresenterId,
                    arbPlayerSubmission: Number(updatedCase.arbPlayerSubmission.replace(/\D/g, '')),
                    teamId: updatedCase.teamId,
                    teamRepId: updatedCase.teamRepId,
                    arbTeamSubmission: Number(updatedCase.arbTeamSubmission.replace(/\D/g, '')),
                    margin: updatedCase.margin,
                    arbHearingDate: updatedCase.arbHearingDate != null ? new Date(updatedCase.arbHearingDate) : null,
                    arbCaseResult: updatedCase.arbCaseResult,
                    clubOptions: updatedCase.clubOptions,
                    arbSettlementDate: updatedCase.arbSettlementDate,
                    arbFinalAmount: Number(updatedCase.arbFinalAmount.replace(/\D/g, '')),
                    arbCaseBonus: updatedCase.arbCaseBonus,
                    arbitratorId: arbitrator.arbitratorId === 0 ? null : arbitrator.arbitratorId,
                    caseVote: arbitrator.caseVote,
                    isChair: arbitrator.isChair,
                    employee: '',
                })
            })
        } else {
            arbCaseData.push({
                id: updatedCase.id,
                season: updatedCase.season,
                playerId: updatedCase.playerId,
                playerRepId: updatedCase.playerRepId,
                playerPresenterId: updatedCase.playerPresenterId,
                arbPlayerSubmission: Number(updatedCase.arbPlayerSubmission.replace(/\D/g, '')),
                teamId: updatedCase.teamId,
                teamRepId: updatedCase.teamRepId,
                arbTeamSubmission: Number(updatedCase.arbTeamSubmission.replace(/\D/g, '')),
                margin: updatedCase.margin,
                arbHearingDate: updatedCase.arbHearingDate != null ? new Date(updatedCase.arbHearingDate) : null,
                arbCaseResult: updatedCase.arbCaseResult,
                clubOptions: updatedCase.clubOptions,
                arbSettlementDate: updatedCase.arbSettlementDate,
                arbFinalAmount: Number(updatedCase.arbFinalAmount.replace(/\D/g, '')),
                arbCaseBonus: updatedCase.arbCaseBonus,
                employee: '',
            })
        }
        let response = await UpsertArbCase(arbCaseData);
        setSaveResponse({
            good: response.status === 200,
            msg: response.status === 200
                ? 'Successfully Saved!'
                : 'Failed to save!'
        });
        refreshAllArbCases();
    }

    async function applyNewPerson(selectedPerson) {
        setNewPerson(null);
        if (selectedPerson == null)
            return;

        if (selectedPerson.id === -1) {
            let personIdAdded = await AddNewPerson(selectedPerson);
            selectedPerson.id = personIdAdded;


            let updatedLookups = Object.assign({}, lookups);
            let personLookupIndex;
            switch (selectedPerson.label) {
                case 'playerPresenter':
                    personLookupIndex = updatedLookups.playerPresenters.findIndex(person => person.key === selectedPerson.id);
                    if (!~personLookupIndex)
                        updatedLookups.playerPresenters.push({ key: selectedPerson.id, title: selectedPerson.name });
                    break;
                case 'playerRep':
                    personLookupIndex = updatedLookups.playerReps.findIndex(person => person.key === selectedPerson.id);
                    if (!~personLookupIndex)
                        updatedLookups.playerReps.push({ key: selectedPerson.id, title: selectedPerson.name });
                    break;
                case 'teamRep':
                    personLookupIndex = updatedLookups.teamPresenters.findIndex(person => person.key === selectedPerson.id);
                    if (!~personLookupIndex)
                        updatedLookups.teamPresenters.push({ key: selectedPerson.id, title: selectedPerson.name });
                    break;
                default:
                    personLookupIndex = updatedLookups.arbitrators.findIndex(person => person.key === selectedPerson.id);
                    if (!~personLookupIndex)
                        updatedLookups.arbitrators.push({ key: selectedPerson.id, title: selectedPerson.name });
                    break;
            }
            setLookups(updatedLookups);
        }

        let newUpdatedCase = Object.assign({}, updatedCase);
        if (selectedPerson.label.includes('arbitrator')) {
            let arbitratorIndex = selectedPerson.label.split('-')[1];
            newUpdatedCase.arbitrators[arbitratorIndex].arbitratorId = selectedPerson.id;
            newUpdatedCase.arbitrators[arbitratorIndex].arbitrator = selectedPerson.name ?? 'No Arbitrator';
        } else {
            newUpdatedCase[selectedPerson.label] = selectedPerson.name ?? (selectedPerson.label.includes('rep') ? 'No Representative' : 'No Presenter');
            newUpdatedCase[selectedPerson.label + 'Id'] = selectedPerson.id;
        }
        setUpdatedCase(newUpdatedCase);
    }

    function refreshAllArbCases() {
        (async function () {
            let allArbCases = await GetAllArbCases();
            let seasonsArbCases = {};
            seasonsArbCases[curSeason.toString()] = [];
            allArbCases
                .filter(arbCase => arbCase.playerId > 0)
                .forEach(arbCase => {
                    if (!Object.hasOwn(seasonsArbCases, arbCase.season.toString()))
                        seasonsArbCases[arbCase.season.toString()] = [];
                    let curArbCase = Object.assign({ key: arbCase.playerId, title: arbCase.player }, arbCase);
                    seasonsArbCases[arbCase.season.toString()].push(curArbCase);
                })
            setAllArbCases(seasonsArbCases);
        })()
    }

    function renderDataEntryForm() {
        return (
            <>
                <Row>
                    <Col sm={12} md={6}>
                        <div style={{ fontWeight: 700 }}>Player</div>
                        <div className='form-box'>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Player: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    {updatedCase.player}
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Representative: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <AutoComplete
                                        items={lookups.playerReps}
                                        onSelect={(personId) => setUpdatedCase(Object.assign({}, updatedCase, { playerRepId: personId }))}
                                        placeholder={'Agency'}
                                        value={updatedCase.playerRep}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Player Presenter: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <AutoComplete
                                        items={lookups.playerPresenters}
                                        onSelect={(personId) => setUpdatedCase(Object.assign({}, updatedCase, { playerPresenterId: personId }))}
                                        placeholder={'Presenter'}
                                        value={updatedCase.playerPresenter}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Submission: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <input
                                        onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { arbPlayerSubmission: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                                        placeholder='$0'
                                        step='50000'
                                        style={{ width: '125px' }}
                                        value={updatedCase.arbPlayerSubmission}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col sm={12} md={6}>
                        <div style={{ fontWeight: 700 }}>Team</div>
                        <div className='form-box'>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Team: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <AutoComplete
                                        items={lookups.teams}
                                        onSelect={(teamId) => setUpdatedCase(Object.assign({}, updatedCase, { teamId: teamId }))}
                                        placeholder={'Team'}
                                        value={updatedCase.team}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Team Presenter: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <AutoComplete
                                        items={lookups.teamPresenters}
                                        onSelect={(personId) => setUpdatedCase(Object.assign({}, updatedCase, { teamRepId: personId }))}
                                        placeholder={'Presenter'}
                                        value={updatedCase.teamRep}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Submission: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <input
                                        onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { arbTeamSubmission: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                                        placeholder='$0'
                                        step='50000'
                                        style={{ width: '125px' }}
                                        value={updatedCase.arbTeamSubmission}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>
                </Row>
                <div style={{ fontWeight: 700 }}>Hearing Info</div>
                <Row className='form-box'>
                    <Col sm={12} md={6} style={{ border: 'solid 1px black', backgroundColor: 'white', padding: '0' }}>
                        <Row style={{ backgroundColor: 'grey', fontWeight: 700 }}>
                            <Col xs={5}>
                                Arbitrator
                            </Col>
                            <Col xs={2} style={{ borderLeft: 'solid 1px lightgrey', fontWeight: 700 }}>
                                Chair
                            </Col>
                            <Col xs={5} style={{ borderLeft: 'solid 1px lightgrey', fontWeight: 700 }}>
                                Voted For
                            </Col>
                        </Row>
                        {
                            [...Array(3)].map((_, index) => {
                                return <ArbitratorRow
                                    arbitrator={(updatedCase.arbitrators && updatedCase.arbitrators.length >= index + 1)
                                        ? updatedCase.arbitrators[index]
                                        : {
                                            arbitrator: '',
                                            arbitratorId: null,
                                            isChair: null,
                                            caseVote: 0,
                                        }
                                    }
                                    index={index}
                                    key={index}
                                    lookups={lookups}
                                    updateArbitrator={(arbitratorIndex, arbitratorId, isChair, caseVote) => {
                                        let arbitrators = [];
                                        if (updatedCase.arbitrators)
                                            arbitrators = updatedCase.arbitrators.slice(0);
                                        if (arbitrators.length < arbitratorIndex + 1) {
                                            for (var arbIndex = arbitrators.length; arbIndex < arbitratorIndex + 1; arbIndex++) {
                                                arbitrators.push({
                                                    arbitrator: '',
                                                    arbitratorId: null,
                                                    isChair: null,
                                                    caseVote: 0,
                                                })
                                            }
                                        }


                                        arbitrators[arbitratorIndex].arbitratorId = arbitratorId;
                                        arbitrators[arbitratorIndex].isChair = isChair;
                                        arbitrators[arbitratorIndex].caseVote = caseVote;

                                        setUpdatedCase(Object.assign({}, updatedCase, { arbitrators: arbitrators }));
                                    }}
                                />
                            })
                        }
                    </Col>
                    {
                        userPermissions.dataWriter &&
                        <Col sm={12} md={6}>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Margin: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    {
                                        updatedCase.margin &&
                                        <Form.Check
                                            defaultChecked
                                            type='checkbox'
                                            onClick={(e) => setUpdatedCase(Object.assign({}, updatedCase, { margin: e.target.checked }))}
                                        />
                                    }
                                    {
                                        !updatedCase.margin &&
                                        <Form.Check
                                            type='checkbox'
                                            onClick={(e) => setUpdatedCase(Object.assign({}, updatedCase, { margin: e.target.checked }))}
                                        />
                                    }
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Hearing Date: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <input
                                        type='date'
                                        value={updatedCase.arbHearingDate
                                            ? customDateString(new Date(updatedCase.arbHearingDate))
                                            : ''
                                        }
                                        onChange={(e) => setUpdatedCase(Object.assign(
                                            {},
                                            updatedCase,
                                            {
                                                arbHearingDate: new Date(
                                                    e.target.value + 'T'
                                                    + (updatedCase.arbHearingDate != null
                                                        ? customTimeString(new Date(updatedCase.arbHearingDate)) + ':00'
                                                        : '00:00:00')
                                                )
                                            }
                                        ))}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={6}>
                                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Hearing Time: </Form.Label>
                                </Col>
                                <Col xs={6}>
                                    <input
                                        type='time'
                                        value={updatedCase.arbHearingDate
                                            ? customTimeString(new Date(updatedCase.arbHearingDate))
                                            : ''
                                        }
                                        onChange={(e) => setUpdatedCase(Object.assign(
                                            {},
                                            updatedCase,
                                            {
                                                arbHearingDate: new Date(
                                                    (updatedCase.arbHearingDate != null
                                                        ? customDateString(new Date(updatedCase.arbHearingDate))
                                                        : customDateString(new Date())
                                                    )
                                                    + 'T' + e.target.value + ':00'
                                                )
                                            }
                                        ))}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    }
                </Row>
                <div style={{ fontWeight: 700 }}>Results</div>
                <Row className='form-box'>
                    <Col sm={12} md={6}>
                        <Row>
                            <Col xs={6}>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Result: </Form.Label>
                            </Col>
                            <Col xs={6}>
                                <Form.Control
                                    as='select'
                                    value={updatedCase.arbCaseResult}
                                    onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { arbCaseResult: e.target.value }))}
                                >
                                    {
                                        lookups.resultTypes.map(resultType => <option
                                            key={resultType.key}
                                            value={resultType.key}
                                        >
                                            {resultType.title}
                                        </option>)
                                    }
                                </Form.Control>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Club Option Years: </Form.Label>
                            </Col>
                            <Col xs={6}>
                                <Form.Control
                                    as='select'
                                    value={updatedCase.clubOptions}
                                    onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { clubOptions: parseInt(e.target.value) }))}
                                >
                                    {
                                        [...Array(6)].map((val, index) => <option
                                            key={index}
                                            value={index}
                                        >
                                            {index}
                                        </option>)
                                    }
                                </Form.Control>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Settlement Date: </Form.Label>
                            </Col>
                            <Col xs={6}>
                                <input
                                    type='date'
                                    value={updatedCase.arbSettlementDate
                                        ? customDateString(new Date(updatedCase.arbSettlementDate))
                                        : ''
                                    }
                                    onChange={(e) => setUpdatedCase(Object.assign(
                                        {},
                                        updatedCase,
                                        {
                                            arbSettlementDate: new Date(
                                                e.target.value + 'T'
                                                + (updatedCase.arbSettlementDate != null
                                                    ? customTimeString(new Date(updatedCase.arbSettlementDate)) + ':00'
                                                    : '00:00:00')
                                            )
                                        }
                                    ))}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Settlement Time: </Form.Label>
                            </Col>
                            <Col xs={6}>
                                <input
                                    type='time'
                                    value={updatedCase.arbSettlementDate
                                        ? customTimeString(new Date(updatedCase.arbSettlementDate))
                                        : ''
                                    }
                                    onChange={(e) => setUpdatedCase(Object.assign(
                                        {},
                                        updatedCase,
                                        {
                                            arbSettlementDate: new Date(
                                                (updatedCase.arbSettlementDate != null
                                                    ? customDateString(new Date(updatedCase.arbSettlementDate))
                                                    : customDateString(new Date())
                                                )
                                                + 'T' + e.target.value + ':00'
                                            )
                                        }
                                    ))}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Final Amount: </Form.Label>
                            </Col>
                            <Col xs={6}>
                                <input
                                    onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { arbFinalAmount: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                                    placeholder='$0'
                                    step='100000'
                                    style={{ width: '125px' }}
                                    value={updatedCase.arbFinalAmount}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6} style={{ fontWeight: 700 }} >
                                P/A:
                            </Col>
                            <Col xs={6}>
                                <Form.Control
                                    as='select'
                                    value={updatedCase.arbCaseBonus}
                                    onChange={(e) => setUpdatedCase(Object.assign({}, updatedCase, { arbCaseBonus: e.target.value }))}
                                >
                                    {
                                        lookups.bonusTypes.map(bonusType => <option
                                            key={bonusType.key}
                                            value={bonusType.key}
                                        >
                                            {bonusType.title}
                                        </option>)
                                    }
                                </Form.Control>
                            </Col>
                        </Row>
                    </Col>
                    <Col sm={12} md={6}>
                        <Form.Label style={{ fontWeight: 700 }}>Contract Summary</Form.Label>
                        <Form.Control as="textarea" defaultValue={updatedCase.contractDescription} readOnly rows={7} />
                    </Col>
                </Row>
                {
                    userPermissions.dataWriter &&
                    <Row>
                        <Button variant={'success'} onClick={() => submitArbCase()}>Save</Button>
                        <Button variant={'warning'} onClick={() => clearData()}>Clear</Button>
                        {
                            saveResponse && !saveResponse.good &&
                            <div style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{saveResponse.msg}</div>
                        }
                        {
                            saveResponse && saveResponse.good &&
                            <div style={{ color: 'rgb(33, 136, 56)', textAlign: 'center' }}>{saveResponse.msg}</div>
                        }
                    </Row>
                }
            </>
        );
    }

    return (
        <div className='body arb-data-entry'>
            <h2>Arbitration Data Entry</h2>
            <br />
            
            <Navbar bg='light' variant='light' className='playerNav'>
                <Nav>
                    <Nav.Link className={tabName === 'individual' ? 'nav-selected' : null} href='#individual' onClick={() => setTabName('individual')}>
                        {
                            tabName === 'individual' &&
                            <span style={{ transform: 'translateX(27px) rotate(135deg)' }}></span>
                        }
                        Individual
                    </Nav.Link>
                    {
                        userPermissions && userPermissions.dataWriter &&
                        <Nav.Link className={tabName === 'bulk' ? 'nav-selected' : null} href='#bulk' onClick={() => setTabName('bulk')}>
                            {
                                tabName === 'bulk' &&
                                <span style={{ transform: 'translateX(7px) rotate(135deg)' }}></span>
                            }
                            Bulk
                        </Nav.Link>
                    }
                </Nav>
            </Navbar>
            <br />
            {
                tabName === 'individual' && allArbCases && caseSelected?.season && lookups &&
                    <Row>
                        <Col xs={12} sm={5}>
                            <Row>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>For Arbitration In: </Form.Label>
                                <Form.Control
                                    style={{ width: '100px' }}
                                    as='select'
                                    defaultValue={caseSelected.season}
                                    onChange={(event) => setCaseSelected({ season: event.target.value, playerId: null })}
                                >
                                    {Object.keys(allArbCases).sort((a, b) => b - a).map(season => {
                                        return <option key={season} value={season}>{season}</option>
                                    })}
                                </Form.Control>
                            </Row>
                        </Col>

                        <Col xs={12} sm={7}>
                            <Row>
                                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>Player: </Form.Label>
                                <AutoComplete
                                    clearAfterSelection={true}
                                    items={allArbCases[caseSelected.season.toString()]}
                                    onSelect={(playerId) => getData(caseSelected.season, playerId)}
                                />
                            </Row>
                        </Col>
                    </Row>
            }
            {
                tabName === 'individual' && updatedCase && userPermissions.dataWriter &&
                    <fieldset>
                        {renderDataEntryForm()}
                    </fieldset>
            }
            {
                tabName === 'individual' && updatedCase && !userPermissions.dataWriter &&
                <fieldset disabled='disabled'>
                    {renderDataEntryForm()}
                </fieldset>
            }
            {
                tabName === 'bulk' && allArbCases && userPermissions.dataWriter &&
                <div className='bulk-entry'>
                    <BulkEntryForm
                        allArbCases={allArbCases}
                        lookups={lookups}
                    />
                </div>
            }

            <NewPersonModal
                applyNewPerson={applyNewPerson}
                lookups={lookups}
                newPerson={newPerson}
                show={newPerson != null}
            />
            <br />
            {
                data?.loading &&
                <div>
                    <br />
                    <br />
                    <i className='fa fa-spinner fa-spin loading-icon'></i>
                    <br />
                </div>
            }
            {
                data?.arbCase &&
                <div>
                </div>
            }
            {
                data?.exceptionMsg &&
                <React.Fragment>
                    <p style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{data.exceptionMsg}</p>
                    <br />
                    <br />
                </React.Fragment>
            }
        </div>
    );
}

export default ArbDataEntry;

function ArbitratorRow(props) {
    const [arbitratorData, setArbitratorData] = useState({
        arbitrator: props.arbitrator.arbitrator,
        arbitratorId: props.arbitrator.arbitratorId,
        caseVote: props.arbitrator.caseVote,
        isChair: props.arbitrator.isChair,
    });

    useEffect(() => {
        props.updateArbitrator(props.index, arbitratorData.arbitratorId, arbitratorData.isChair, arbitratorData.caseVote)
    }, [arbitratorData])


    return <Row style={{ borderTop: 'solid 1px lightgrey' }}>
        <Col xs={5}>
            <AutoComplete
                items={props.lookups.arbitrators}
                onSelect={(personId) => setArbitratorData(Object.assign({}, arbitratorData, { arbitratorId: personId }))}
                placeholder={`Arbitrator ${props.index + 1}`}
                value={arbitratorData.arbitrator !== props.arbitrator.arbitrator ? props.arbitrator.arbitrator : arbitratorData.arbitrator}
            />
        </Col>
        <Col xs={2} style={{ borderLeft: 'solid 1px lightgrey' }}>
            {
                (arbitratorData.isChair !== props.arbitrator.isChair ? props.arbitrator.isChair : arbitratorData.isChair) &&
                <Form.Check
                    defaultChecked
                    type='checkbox'
                    onClick={(e) => setArbitratorData(Object.assign({}, arbitratorData, { isChair: e.target.checked }))}
                />
            }
            {
                (arbitratorData.isChair !== props.arbitrator.isChair ? !props.arbitrator.isChair : !arbitratorData.isChair) &&
                <Form.Check
                    type='checkbox'
                    onClick={(e) => setArbitratorData(Object.assign({}, arbitratorData, { isChair: e.target.checked }))}
                />
            }
        </Col>
        <Col xs={5} style={{ borderLeft: 'solid 1px lightgrey' }}>
            <Form.Control
                as='select'
                value={arbitratorData.caseVote !== props.arbitrator.caseVote ? props.arbitrator.caseVote : arbitratorData.caseVote}
                onChange={(e) => setArbitratorData(Object.assign({}, arbitratorData, { caseVote: e.target.value }))}
            >
                <option key='0' value='0'>No Vote</option>
                <option key='1' value='1'>Player</option>
                <option key='2' value='2'>Team</option>
            </Form.Control>
        </Col>
    </Row>
}

function BulkEntryForm(props) {
    const [casesToDelete, setCasesToDelete] = useState([]);
    const [casesToUpdate, setCasesToUpdate] = useState(props.allArbCases[curSeason].slice(0));
    const [saveResponse, setSaveResponse] = useState(null);
    const [season, setSeason] = useState(curSeason);

    useEffect(() => {
        setCasesToDelete([]);
        setCasesToUpdate(props.allArbCases[season.toString()].slice(0))
    }, [season])

    async function updateArbCases() {
        setSaveResponse(null);
        let arbCases = {
            casesToDelete: [],
            casesToUpdate: [],
        };

        casesToUpdate.forEach(caseToUpdate => {
            arbCases.casesToUpdate.push({
                id: caseToUpdate.id,
                season: caseToUpdate.season,
                playerId: caseToUpdate.playerId,
                arbPlayerSubmission: Number(caseToUpdate.arbPlayerSubmission.toString().replace(/\D/g, '')),
                arbTeamSubmission: Number(caseToUpdate.arbTeamSubmission.toString().replace(/\D/g, '')),
                arbCaseResult: caseToUpdate.arbCaseResult,
                arbFinalAmount: Number(caseToUpdate.arbFinalAmount.toString().replace(/\D/g, '')),
                employee: '',
            })
        })

        casesToDelete.forEach(caseToDelete => {
            arbCases.casesToDelete.push({
                id: caseToDelete.id,
                season: caseToDelete.season,
                playerId: caseToDelete.playerId,
                arbPlayerSubmission: Number(caseToDelete.arbPlayerSubmission.toString().replace(/\D/g, '')),
                arbTeamSubmission: Number(caseToDelete.arbTeamSubmission.toString().replace(/\D/g, '')),
                arbCaseResult: caseToDelete.arbCaseResult,
                arbFinalAmount: Number(caseToDelete.arbFinalAmount.toString().replace(/\D/g, '')),
                employee: '',
            })
        })
        let deleteResponse = await BulkDeleteArbCase(arbCases.casesToDelete);
        let upsertResponse = await BulkUpsertArbCase(arbCases.casesToUpdate);
        setSaveResponse({
            delete: {
                good: deleteResponse.status === 200,
                msg: deleteResponse.status === 200
                    ? 'Successfully Deleted!'
                    : 'Failed to delete!'
            },
            upsert: {
                good: upsertResponse.status === 200,
                msg: upsertResponse.status === 200
                    ? 'Successfully Saved!'
                    : 'Failed to save!'
            }
        });
    }

    async function generateArbCases() {
        let players = await GenerateArbCases(season);
        let newCases = players.map(player => {
            return {
                id: 0,
                season: season,
                player: player.lastName + ', ' + player.commonName,
                playerId: player.playerId,
                arbPlayerSubmission: 0,
                arbTeamSubmission: 0,
                arbCaseResult: 0,
                arbFinalAmount: 0,
            };
        });
        setCasesToUpdate(newCases);
    }

    function addNewCase() {
        let newCasesToUpdate = casesToUpdate.slice(0);
        newCasesToUpdate.push({
            id: 0,
            season: season,
            player: '',
            playerId: 0,
            arbPlayerSubmission: 0,
            arbTeamSubmission: 0,
            arbCaseResult: 0,
            arbFinalAmount: 0,
        });
        setCasesToUpdate(newCasesToUpdate);
    }

    function markForDeletion(index, shouldDelete) {
        let updatedCasesToDelete = casesToDelete.map(caseToDelete => Object.assign({}, caseToDelete));
        let updatedCasesToUpdate = casesToUpdate.map(caseToUpdate => Object.assign({}, caseToUpdate));
        if (shouldDelete) {
            let caseToDelete = updatedCasesToUpdate[index];
            if (caseToDelete.id === 0)
                updatedCasesToUpdate = updatedCasesToUpdate.toSpliced(index, 1);
            else {
                updatedCasesToUpdate = updatedCasesToUpdate.filter(curCase => curCase.id !== caseToDelete.id);
                updatedCasesToDelete.push(caseToDelete);
            }
        } else {
            let caseToRevert = updatedCasesToDelete[index];
            updatedCasesToDelete = updatedCasesToDelete.filter(curCase => curCase.id !== caseToRevert.id);
            updatedCasesToUpdate.push(caseToRevert);
        }
        setCasesToDelete(updatedCasesToDelete);
        setCasesToUpdate(updatedCasesToUpdate);
    }

    function updateCase(index, id, season, player, playerId, arbPlayerSubmission, arbTeamSubmission, arbCaseResult, arbFinalAmount) {
        let prevCasesToUpdate = casesToUpdate.slice(0);
        let caseToUpdate = Object.assign({}, prevCasesToUpdate[index]);

        caseToUpdate.id = id;
        caseToUpdate.season = season;
        caseToUpdate.player = player;
        caseToUpdate.playerId = playerId;
        caseToUpdate.arbPlayerSubmission = arbPlayerSubmission;
        caseToUpdate.arbTeamSubmission = arbTeamSubmission;
        caseToUpdate.arbCaseResult = arbCaseResult;
        caseToUpdate.arbFinalAmount = arbFinalAmount;

        prevCasesToUpdate[index] = caseToUpdate;

        setCasesToUpdate(prevCasesToUpdate);
    }

    return (
        <>
            <div style={{ position: 'fixed', zIndex: 999, left: '900px' }}>
                <Button variant={'success'} onClick={() => updateArbCases()}>Save</Button>
                <Row>
                    {
                        saveResponse && !saveResponse.delete.good &&
                        <div style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{saveResponse.delete.msg}</div>
                    }
                    {
                        saveResponse && saveResponse.delete.good &&
                        <div style={{ color: 'rgb(33, 136, 56)', textAlign: 'center' }}>{saveResponse.delete.msg}</div>
                    }
                </Row>
                <Row>
                    {
                        saveResponse && !saveResponse.upsert.good &&
                        <div style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{saveResponse.upsert.msg}</div>
                    }
                    {
                        saveResponse && saveResponse.upsert.good &&
                        <div style={{ color: 'rgb(33, 136, 56)', textAlign: 'center' }}>{saveResponse.upsert.msg}</div>
                    }
                </Row>
            </div>
            <Row>
                <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>For Arbitration In: </Form.Label>
                <Form.Control
                    style={{ width: '100px' }}
                    as='select'
                    defaultValue={season}
                    onChange={(event) => setSeason(event.target.value)}
                >
                    {Object.keys(props.allArbCases).sort((a, b) => b - a).map(season => {
                        return <option key={season} value={season}>{season}</option>
                    })}
                </Form.Control>
                {
                    casesToUpdate && casesToUpdate.length === 0 &&
                    <Button variant={'primary'} onClick={() => generateArbCases()}>Generate</Button>
                }
                {
                    casesToUpdate && casesToUpdate.length > 0 &&
                    <div style={{textAlign: 'center' }}>{casesToUpdate.length} cases</div>
                }
                {
                    saveResponse && !saveResponse.good &&
                    <div style={{ color: 'rgb(220, 53, 69)', textAlign: 'center' }}>{saveResponse.msg}</div>
                }
                {
                    saveResponse && saveResponse.good &&
                    <div style={{ color: 'rgb(33, 136, 56)', textAlign: 'center' }}>{saveResponse.msg}</div>
                }
            </Row>
            <Row style={{ flexWrap: 'nowrap' }}>
                <div style={{ flexBasis: '200px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Player
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Player Submission
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Team Submission
                </div>
                <div style={{ flexBasis: '170px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Result
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Final Amount
                </div>
            </Row>
            <fieldset>
                {casesToUpdate && season && props.lookups &&
                    casesToUpdate.map((curCase, index) =>
                        <BulkEntryRow
                            case={curCase}
                            index={index}
                            key={curCase.id}
                            lookups={props.lookups}
                            markForDeletion={markForDeletion}
                            markedForDeletion={false}
                            players={props.lookups.players[season.toString()]}
                            updateCase={updateCase}
                        />
                    )
                }
                <Row>
                    <Button variant={'primary'} onClick={() => addNewCase()}>
                        Add Case
                    </Button>
                </Row>
            </fieldset>
            <br />
            <Row><h4 style={{ margin: 'auto' }}>Cases To Delete</h4></Row>
            <Row style={{ flexWrap: 'nowrap' }}>
                <div style={{ flexBasis: '200px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Player
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Player Submission
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Team Submission
                </div>
                <div style={{ flexBasis: '170px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Result
                </div>
                <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px', fontWeight: 700 }}>
                    Final Amount
                </div>
            </Row>
            <fieldset disabled='disabled'>
                {casesToDelete && season && props.lookups &&
                    casesToDelete.map((curCase, index) =>
                        <BulkEntryRow
                            case={curCase}
                            index={index}
                            lookups={props.lookups}
                            markForDeletion={markForDeletion}
                            markedForDeletion={true}
                            players={props.lookups.players[season.toString()]}
                        />
                    )
                }
            </fieldset>
        </>
    )
}

function BulkEntryRow(props) {
    const [caseData, setCaseData] = useState({
        id: props.case.id,
        season: props.case.season,
        player: props.case.player,
        playerId: props.case.playerId,
        arbPlayerSubmission: props.case.arbPlayerSubmission.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        arbTeamSubmission: props.case.arbTeamSubmission.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','),
        arbHearingDate: props.case.arbHearingDate,
        arbCaseResult: props.case.arbCaseResult,
        arbSettlementDate: props.case.arbSettlementDate,
        arbFinalAmount: props.case.arbFinalAmount.toString().replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    });
    const [isShaking, setIsShaking] = useState(false);

    useEffect(() => {
        if (props.updateCase) {
            props.updateCase(
                props.index,
                caseData.id,
                caseData.season,
                caseData.player,
                caseData.playerId,
                caseData.arbPlayerSubmission,
                caseData.arbTeamSubmission,
                caseData.arbCaseResult,
                caseData.arbFinalAmount,
            );
        }
    }, [caseData])

    return (
        <div className='bulk-entry-row row' key={props.case.id} style={{ flexWrap: 'nowrap' }}>
            <div style={{ flexBasis: '200px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}>
                <AutoComplete
                    items={props.players}
                    onSelect={(personId) => setCaseData(Object.assign({}, caseData, { playerId: personId }))}
                    value={caseData.player !== props.case.player ? props.case.player : caseData.player}
                />
            </div>
            <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}>
                <input
                    onChange={(e) => setCaseData(Object.assign({}, caseData, { arbPlayerSubmission: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                    placeholder='Player'
                    style={{ width: '100px', textAlign: 'right' }}
                    value={caseData.arbPlayerSubmission == 0 ? null : caseData.arbPlayerSubmission}
                />
            </div>
            <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}>
                <input
                    onChange={(e) => setCaseData(Object.assign({}, caseData, { arbTeamSubmission: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                    placeholder='Team'
                    style={{ width: '100px', textAlign: 'right' }}
                    value={caseData.arbTeamSubmission == 0 ? null : caseData.arbTeamSubmission}
                />
            </div>
            <div style={{ flexBasis: '170px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}>
                <Form.Control
                    as='select'
                    value={caseData.arbCaseResult}
                    onChange={(e) => setCaseData(Object.assign({}, caseData, { arbCaseResult: e.target.value }))}
                >
                    {
                        props.lookups.resultTypes.map(resultType => <option
                            key={resultType.key}
                            value={resultType.key}
                        >
                            {resultType.title}
                        </option>)
                    }
                </Form.Control>
            </div>
            <div style={{ flexBasis: '100px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}>
                <input
                    onChange={(e) => setCaseData(Object.assign({}, caseData, { arbFinalAmount: e.target.value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',') }))}
                    placeholder='Final'
                    style={{ width: '100px', textAlign: 'right' }}
                    value={caseData.arbFinalAmount == 0 ? null : caseData.arbFinalAmount}
                />
            </div>
            {
                !props.markedForDeletion &&
                <div
                    onClick={() => {
                        setIsShaking(true);
                        setTimeout(() => {
                            setIsShaking(false);
                            props.markForDeletion(props.index, !props.markedForDeletion)
                        }, 200);
                    }}
                    style={{ cursor: 'pointer', flexBasis: '50px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}
                >
                    <FontAwesomeIcon icon={faTrash} className={isShaking ? 'shake' : ''} />
                </div>
            }
            {
                props.markedForDeletion &&
                <div
                    onClick={() => {
                        setIsShaking(true);
                        setTimeout(() => {
                            setIsShaking(false);
                            props.markForDeletion(props.index, !props.markedForDeletion)
                        }, 200);
                    }}
                    style={{ cursor: 'pointer', flexBasis: '50px', flexGrow: 0, flexShrink: 0, marginRight: '15px' }}
                >
                    <FontAwesomeIcon icon={faTrashRestore} className={isShaking ? 'shake' : ''} />
                </div>
            }
        </div>
    )
}

function NewPersonModal(props) {
    const [name, setName] = useState('');
    const [potentialMatches, setPotentialMatches] = useState(null);
    const [selectedMatch, setSelectedMatch] = useState(null);

    async function createNewPerson() {
        let newPersonObj = Object.assign({}, props.newPerson, { name: name });
        let peopleToCompare = [];

        switch (props.newPerson?.label) {
            case 'playerPresenter':
                newPersonObj.personType = 1;
                peopleToCompare = props.lookups.playerPresenters;
                break;
            case 'playerRep':
                newPersonObj.personType = 2;
                peopleToCompare = props.lookups.playerReps;
                break;
            case 'teamRep':
                newPersonObj.personType = 3;
                peopleToCompare = props.lookups.teamPresenters;
                break;
            default:
                newPersonObj.personType = 0;
                peopleToCompare = props.lookups.arbitrators;
                break;
        }

        const fuseOptions = {
            ignoreLocation: true,
            includeScore: true,
        }
        const fuse = new Fuse(peopleToCompare.map(person => person.title), fuseOptions);
        const fuseMatches = fuse.search(name)
            //.filter(match => match.score <= 0.3)
            .map(match => Object.assign({}, match, { id: peopleToCompare[match.refIndex].key }))
            .slice(0, 9);

        if (fuseMatches.length > 0 && selectedMatch == null) {
            setPotentialMatches(fuseMatches);
        } else {
            let selectedPerson = Object.assign(newPersonObj, selectedMatch);
            setName('');
            setPotentialMatches(null);
            setSelectedMatch(null);
            props.applyNewPerson(selectedPerson);
        }
    }

    function getInputLabel() {
        switch (props.newPerson?.label) {
            case 'playerRep':
                return 'New Player Representative';
            case 'playerPresenter':
                return 'New Player Presenter';
            case 'teamRep':
                return 'New Team Representative';
            default:
                return 'New Arbitrator';
        }
    }

    return (
        <Modal show={props.show} onHide={() => props.applyNewPerson(null)}>
            <Modal.Header closeButton>
                <Modal.Title>Modal title</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row>
                    <Form.Label style={{ fontWeight: 700, marginBottom: 0, paddingRight: '10px' }}>{getInputLabel()}: </Form.Label>
                    <Form.Control
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        placeholder={getInputLabel()}
                    />
                </Row>
                {
                    potentialMatches &&
                    <>
                        <br />
                        <Form onChange={(event) => {
                            if (!isNaN(event.target.value)) {
                                setSelectedMatch({ id: Number(event.target.value), name: event.target.labels[0].innerText });
                            }
                        }}>
                            <Row style={{ fontWeight: 700 }}>Create/Confirm New</Row>
                            <Form.Check
                                custom
                                name='statType'
                                label={name}
                                type='radio'
                                id={-1}
                                value={-1}
                            />
                            <Row style={{ fontWeight: 700 }}>Possible Existing Matches</Row>
                            {
                                potentialMatches.map(match =>
                                    <Form.Check
                                        custom
                                        name='statType'
                                        label={match.item + ' - ' + match.score}
                                        type='radio'
                                        id={match.id}
                                        value={match.id}
                                    />
                                )
                            }
                        </Form>
                    </>
                }
            </Modal.Body>

            <Modal.Footer>
                <Button onClick={() => props.applyNewPerson(null)} variant="secondary">Close</Button>
                <Button onClick={() => createNewPerson()} variant="primary">{
                    selectedMatch == null
                        ? 'Create'
                        : selectedMatch.id > 0
                            ? 'Use Existing'
                            : 'Confirm'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}